// Fuzzy subsequence matching for the command palette.
//
// 06-qol §1 is explicit: fuzzy, not prefix — typing "sortacc" must find
// "Sorting mode — Senior accountant" — and the palette must open and filter in
// under 100ms. So this is a dependency-free scorer sized for that budget.
//
// The scoring is a dynamic program: `best[i][j]` is the best score for matching
// the first i+1 query characters with query[i] landing on target[j]. The naive
// form is O(query x target^2) because each cell scans every earlier match. Keeping
// the gap penalty *linear* collapses that to O(query x target): a running maximum
// of `score + position * GAP_PENALTY` carries the best predecessor forward, and
// subtracting `position * GAP_PENALTY` at the current cell recovers the real value.
// A 60-character label against a 10-character query is then ~600 operations, so a
// registry of several hundred commands filters in well under a millisecond.

/** Every matched character is worth this before bonuses. */
const MATCH = 16;
/** Adjacent to the previous match — "sor" in "sorting" beats "s...o...r". */
const CONSECUTIVE = 12;
/** First character of a word: preceded by a space, dash, slash, dot or em dash. */
const WORD_BOUNDARY = 10;
/** A lowercase-to-uppercase step, so "SA" finds "SeniorAccountant". */
const CAMEL_BOUNDARY = 8;
/** The very start of the string. */
const LEADING = 6;
/** Charged per skipped character. Linear, which is what keeps the DP linear. */
const GAP_PENALTY = 1;

const UNREACHABLE = -1_000_000;

const BOUNDARY_CHARS = new Set([' ', '-', '_', '/', '.', ',', '(', '[', ':', '—', '·']);

export interface FuzzyMatch {
  /** Higher is better. Only meaningful when comparing matches on the same query. */
  readonly score: number;
  /** Indices into the original target string, ascending — for highlighting. */
  readonly indices: readonly number[];
}

function bonusAt(target: string, lower: string, index: number): number {
  if (index === 0) return LEADING + WORD_BOUNDARY;
  if (BOUNDARY_CHARS.has(target[index - 1])) return WORD_BOUNDARY;
  // Camel boundary: this character is uppercase and the one before it is not.
  const isUpperHere = target[index] !== lower[index];
  const previousIsLower = target[index - 1] === lower[index - 1];
  if (isUpperHere && previousIsLower) return CAMEL_BOUNDARY;
  return 0;
}

/**
 * Returns `null` when `query` is not a subsequence of `target`. An empty query
 * matches everything with a score of zero, which is what lets the palette show its
 * full contents before anyone types.
 *
 * Case-insensitive. `indices` point into `target` as given, so highlighting keeps
 * the original casing.
 */
export function fuzzyMatch(query: string, target: string): FuzzyMatch | null {
  const q = query.trim().toLowerCase();
  if (q === '') return { score: 0, indices: [] };
  if (q.length > target.length) return null;

  const lower = target.toLowerCase();
  const qn = q.length;
  const tn = target.length;

  const score = new Int32Array(qn * tn).fill(UNREACHABLE);
  // Where the previous query character matched, so the run can be walked back.
  const from = new Int32Array(qn * tn).fill(-1);

  for (let i = 0; i < qn; i += 1) {
    const row = i * tn;
    const previousRow = row - tn;

    // Running best of (previous row's score at k) + k * GAP_PENALTY, for k < j.
    let carriedScore = UNREACHABLE;
    let carriedIndex = -1;

    for (let j = 0; j < tn; j += 1) {
      if (i > 0 && j > 0) {
        const candidate = score[previousRow + j - 1];
        if (candidate > UNREACHABLE) {
          const adjusted = candidate + (j - 1) * GAP_PENALTY;
          if (adjusted > carriedScore) {
            carriedScore = adjusted;
            carriedIndex = j - 1;
          }
        }
      }

      if (q[i] !== lower[j]) continue;

      if (i === 0) {
        // Later starts are mildly discouraged so an early hit wins a tie.
        score[row + j] = MATCH + bonusAt(target, lower, j) - j * GAP_PENALTY;
        from[row + j] = -1;
        continue;
      }

      let best = UNREACHABLE;
      let bestFrom = -1;

      // Adjacent to the previous match: no gap, and the consecutive bonus applies.
      const adjacent = score[previousRow + j - 1];
      if (j > 0 && adjacent > UNREACHABLE) {
        best = adjacent + MATCH + CONSECUTIVE;
        bestFrom = j - 1;
      }

      // Anything further back, recovered from the running maximum.
      if (carriedIndex >= 0 && carriedIndex < j - 1) {
        const gapped = carriedScore - j * GAP_PENALTY + MATCH + bonusAt(target, lower, j);
        if (gapped > best) {
          best = gapped;
          bestFrom = carriedIndex;
        }
      }

      if (best > UNREACHABLE) {
        score[row + j] = best;
        from[row + j] = bestFrom;
      }
    }
  }

  // Best ending position for the final query character.
  const lastRow = (qn - 1) * tn;
  let bestEnd = -1;
  let bestScore = UNREACHABLE;
  for (let j = 0; j < tn; j += 1) {
    if (score[lastRow + j] > bestScore) {
      bestScore = score[lastRow + j];
      bestEnd = j;
    }
  }
  if (bestEnd < 0 || bestScore <= UNREACHABLE) return null;

  const indices: number[] = new Array(qn);
  let position = bestEnd;
  for (let i = qn - 1; i >= 0; i -= 1) {
    indices[i] = position;
    position = from[i * tn + position];
  }

  return { score: bestScore, indices };
}

export interface FuzzyResult<TItem> {
  readonly item: TItem;
  readonly score: number;
  readonly indices: readonly number[];
}

/**
 * Filters and ranks a list in one pass. `tiebreak` decides the order of equal
 * scores — the palette uses it to keep its registration order stable, so results
 * never shuffle between keystrokes that score the same.
 */
export function fuzzyFilter<TItem>(
  query: string,
  items: readonly TItem[],
  textOf: (item: TItem) => string,
  tiebreak?: (a: TItem, b: TItem) => number,
): FuzzyResult<TItem>[] {
  const results: FuzzyResult<TItem>[] = [];
  for (const item of items) {
    const match = fuzzyMatch(query, textOf(item));
    if (match) results.push({ item, score: match.score, indices: match.indices });
  }
  results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return tiebreak ? tiebreak(a.item, b.item) : 0;
  });
  return results;
}

export interface HighlightSegment {
  readonly text: string;
  readonly matched: boolean;
}

/** Splits a label into matched and unmatched runs, so the palette can bold the hits. */
export function highlightSegments(
  text: string,
  indices: readonly number[],
): readonly HighlightSegment[] {
  if (indices.length === 0) return [{ text, matched: false }];

  const hit = new Set(indices);
  const segments: HighlightSegment[] = [];
  let start = 0;
  let current = hit.has(0);

  for (let i = 1; i <= text.length; i += 1) {
    const isMatch = i < text.length && hit.has(i);
    if (isMatch === current && i < text.length) continue;
    segments.push({ text: text.slice(start, i), matched: current });
    start = i;
    current = isMatch;
  }
  return segments;
}
